import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customer invoices tab — `GET /customers/:id/invoices` (§5.6) + the 4-card
 * statistical summary. Field names mirror the backend `GetCustomerInvoicesRow` /
 * `GetCustomerInvoiceStatisticalRow`. List filters (invoice_status / invoice_type
 * / service_id) go base64 into `filters`.
 */

export type InvoiceStatus =
  | 'waiting-confirm'
  | 'approved'
  | 'paid'
  | 'cancelled'
  | 'rejected'
  | 'done'
  | 'refunded';

export interface InvoiceRow {
  invoice_id: number;
  invoice_total: number;
  invoice_apply_startdate?: string;
  invoice_apply_enddate?: string;
  invoice_duedate?: string;
  invoice_status: string;
  invoice_type?: string;
  invoice_description?: string;
  invoice_payment_at?: string;
  invoice_payment_method?: string;
  invoice_sale_type?: string;
  created_at?: string;
  invoice_items?: unknown[];
  service?: unknown;
}

/**
 * One statistical bucket. The backend groups by status and returns the summed
 * amount as `total_amount` (BIGINT) plus a row `count` — NOT `total`.
 */
export interface InvoiceStatisticalRow {
  invoice_status: string;
  total_amount: number;
  count?: number;
}

export interface InvoicesParams {
  page?: number;
  page_size?: number;
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: Record<string, unknown>;
}

function invoicesQuery(params: InvoicesParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/** `GET /customers/:id/invoices` — the invoices table for one customer. */
export function useCustomerInvoices(id: number, params: InvoicesParams = {}) {
  return useQuery({
    queryKey: ['customers', 'invoices', id, params],
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<InvoiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<InvoiceRow>>(
        `/customers/${id}/invoices?${invoicesQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/:id/invoices/statistical` — per-status totals for the cards. */
export function useCustomerInvoiceStatistical(id: number) {
  return useQuery({
    queryKey: ['customers', 'invoices', id, 'statistical'],
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<InvoiceStatisticalRow>> => {
      const res = await dataCentralApi.get<ResponseArray<InvoiceStatisticalRow>>(
        `/customers/${id}/invoices/statistical`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ─────────────────────────────────────────────────────────── */

/**
 * `POST /invoices/merge` — merge a list of waiting-confirm or approved invoices
 * into one. Backend expects `{ invoice_ids: number[] }`. Verified endpoint exists
 * (returns 400 when called without a valid id list, not 404).
 */
export function useMergeInvoices(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceIds: number[]): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>('/invoices/merge', {
        invoice_ids: invoiceIds,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', 'invoices', customerId] });
    },
  });
}
